import React, { useState, useEffect } from 'react';
import {
  Alert,
  CircularProgress,
  Typography,
  Box,
  Paper,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Divider,
  Container,
} from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from './contexts/AuthContext';
import { sendEmailVerification } from 'firebase/auth';
import { useFirebase } from './contexts/FirebaseContext';
import { deleteUserAccount } from './utilities/authUtils';

import { SecurityOverview } from './components/settings/SecurityOverview';
import { ProfileSection } from './components/settings/ProfileSection';
import AccountLinking from './components/AccountLinking';

function Settings() {
  const { currentUser, userProfile, updateUserProfile } = useAuth();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deletePassword, setDeletePassword] = useState('');
  const [deleteLoading, setDeleteLoading] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { db } = useFirebase();

  useEffect(() => {
    if (location.state?.message) {
      setSuccess(location.state.message);
      navigate(location.pathname, { replace: true });
    }
    setLoading(false);
  }, [location, navigate]);

  const handleResendVerification = async () => {
    setLoading(true);
    setError(null);

    try {
      await sendEmailVerification(currentUser);
      setSuccess('Verification email has been sent. Please check your inbox.');
    } catch (error) {
      console.error('Error sending verification email:', error);
      setError('Failed to send verification email. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteAccount = async () => {
    setDeleteLoading(true);
    setError(null);

    try {
      if (!db) {
        throw new Error('Firestore instance not available');
      }
      await deleteUserAccount(currentUser, db, userProfile, deletePassword);
      setShowDeleteDialog(false);
    } catch (error) {
      console.error('Error deleting account:', error);
      if (error.code === 'auth/wrong-password') {
        setError('Incorrect password');
      } else if (error.code === 'auth/requires-recent-login') {
        setError(
          'For security reasons, please sign out and sign in again before deleting your account.'
        );
      } else {
        setError('Failed to delete account. Please try again later.');
      }
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleProfileSubmit = async (profileData) => {
    setLoading(true);
    setError(null);
    try {
      await updateUserProfile(profileData);
      setSuccess('Username updated successfully');
    } catch (error) {
      console.error('Error updating username:', error);
      setError('Failed to update username. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <CircularProgress />;
  }

  return (
    <Box sx={{ display: 'flex' }}>
      <Container maxWidth="sm">
        <Paper variant="rightPane">
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {success && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {success}
            </Alert>
          )}

          <ProfileSection
            handleResendVerification={handleResendVerification}
            loading={loading}
            onSubmit={handleProfileSubmit}
          />

          <Divider sx={{ my: 3 }} />

          <AccountLinking />

          <Divider sx={{ my: 3 }} />

          <SecurityOverview onResendVerification={handleResendVerification} />

          <Divider sx={{ my: 3 }} />

          {/* Delete Account Section */}
          <Box sx={{ mt: 4 }}>
            <Typography variant="sectionHeader" component="h3">
              Delete Account
            </Typography>
            <Typography variant="body2" color="text.secondary" component="p">
              Warning: This action cannot be undone. All your data will be permanently deleted.
            </Typography>
            <Button
              variant="contained"
              color="error"
              onClick={() => setShowDeleteDialog(true)}
              disabled={loading || deleteLoading}
              sx={{ mt: 2 }}
            >
              Delete Account
            </Button>
          </Box>

          {/* Delete Account Dialog */}
          <Dialog
            open={showDeleteDialog}
            onClose={() => {
              setShowDeleteDialog(false);
              setDeletePassword('');
              setError(null);
            }}
          >
            <DialogTitle>Delete Account</DialogTitle>
            <DialogContent>
              <Typography component="p">
                Are you sure you want to delete your account? This action cannot be undone.
              </Typography>
              {currentUser?.providerData?.some(
                (provider) => provider.providerId === 'password'
              ) && (
                <TextField
                  margin="normal"
                  required
                  fullWidth
                  name="password"
                  label="Confirm your password"
                  type="password"
                  value={deletePassword}
                  onChange={(e) => setDeletePassword(e.target.value)}
                  disabled={deleteLoading}
                />
              )}
              {error && (
                <Alert severity="error" sx={{ mt: 2 }}>
                  {error}
                </Alert>
              )}
            </DialogContent>
            <DialogActions>
              <Button
                onClick={() => {
                  setShowDeleteDialog(false);
                  setDeletePassword('');
                  setError(null);
                }}
                disabled={deleteLoading}
              >
                Cancel
              </Button>
              <Button
                onClick={handleDeleteAccount}
                variant="contained"
                color="error"
                disabled={
                  deleteLoading ||
                  (currentUser?.providerData?.some(
                    (provider) => provider.providerId === 'password'
                  ) &&
                    !deletePassword)
                }
                startIcon={deleteLoading ? <CircularProgress size={20} /> : null}
              >
                Delete Account
              </Button>
            </DialogActions>
          </Dialog>
        </Paper>
      </Container>
    </Box>
  );
}

export default Settings;
